import { useSearchParams, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import userDeviceList, { IFilters } from "../../state/hooks/users/userDeviceList";
import { FiltersComponent } from "../../commons/FiltersComponent/FiltersComponent";

interface Device {
  id: string;
  createdAt: string;
  source: string;
  fcmToken: string;
  os: string;
  deviceUniqueId: string;
  model: string;
  browser: string;
  sourceDevice: string;
}

const filterOptions = [
  { value: "source", label: "Source" },
  { value: "fcmToken", label: "FCM Token" },
  { value: "os", label: "OS" },
  { value: "model", label: "Model" },
  { value: "browser", label: "Browser" },
  { value: "sourceDevice", label: "Source Device" },
  { value: "deviceUniqueld", label: "Device Unique ID" },
];

const DeviceList = () => {
  const [searchParams] = useSearchParams();
  const { id: pathId } = useParams<{ id: string }>();
  const queryUserId = searchParams.get("subUserId") || searchParams.get("id");
  const userId = pathId || queryUserId;
  
  const [filtersCount, setFiltersCount] = useState<IFilters>({});
  const [activeFilters, setActiveFilters] = useState<IFilters>({});
  
  const { data, isLoading,isError, error } = userDeviceList(userId || "");
  const allDevices: Device[] = data?.data || [];
  
  const applyClientSideFilter = (devices: Device[], filters: IFilters) => {
    return devices.filter((device) => {
      if (filters.source && device.source?.toLowerCase() !== filters.source.toLowerCase()) {
        return false;
      }
      if (filters.fcmToken && !device.fcmToken?.toLowerCase().includes(filters.fcmToken.toLowerCase())) {
        return false;
      }
      if (filters.os && !device.os?.toLowerCase().includes(filters.os.toLowerCase())) {
        return false;
      }
      if (filters.model && !device.model?.toLowerCase().includes(filters.model.toLowerCase())) {
        return false;
      }
      if (filters.browser && !device.browser?.toLowerCase().includes(filters.browser.toLowerCase())) {
        return false;
      }
      if (filters.sourceDevice && !device.sourceDevice?.toLowerCase().includes(filters.sourceDevice.toLowerCase())) {
        return false;
      }
      if (filters.deviceUniqueld && !device.deviceUniqueId?.toLowerCase().includes(filters.deviceUniqueld.toLowerCase())) {
        return false;
      }
      return true;
    });
  };
  
  const devices = Object.keys(activeFilters).length > 0 
    ? applyClientSideFilter(allDevices, activeFilters)
    : allDevices;
  
  const handleSearch = () => {
    setActiveFilters(filtersCount);
  };

  const DEBOUNCE_MS = 500;
  useEffect(() => {
    const hasAnyFilterValue = Object.values(filtersCount).some(
      (value) => value !== "" && value !== undefined
    );

    if (Object.keys(filtersCount).length === 0 || !hasAnyFilterValue) {
      setActiveFilters({});
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setActiveFilters(filtersCount);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [filtersCount]);

  return (
    <div className="flex px-30 pt-55 pb-20 flex-col bg-light-grey-1 h-full max-w-1200">
      <h2 className="text-dark-grey font-bold text-2xl mb-30">Devices List</h2>

      <FiltersComponent
        setFiltersCount={setFiltersCount}
        filterOptions={filterOptions}
        filtersCount={filtersCount}
        onSearch={handleSearch}
       
      />

      <h3 className="text-lg font-bold mb-[20px] border-b border-[#d5d5d5] text-dark-grey">
        Devices ({devices.length})
      </h3>

      {isLoading ? (
        <div className="text-center py-40 text-gray-500">
          <p>Loading devices...</p>
        </div>
      ) : isError ? (
          <div className="p-20">
            Error:{" "}
            {error instanceof Error ? error.message : "Something went wrong"}
          </div>
        )  : devices.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl border border-light-grey-3 border-solid">
          <table className="min-w-full border-collapse">
            {devices.map((device) => (
              <tbody key={device.id} className="bg-white">
                <tr>
                  <td
                    colSpan={2}
                    className="px-4 py-4 font-bold text-[24px] text-gray-700 border-b border-light-grey-3"
                  >
                    Created: {device.createdAt ? new Date(device.createdAt).toLocaleDateString() : 'Unknown'} 
                  </td>
                </tr>

                <tr>
                  <td className=" w-1/4 px-4 py-6 font-bold text-[16px] text-gray-700 border-b border-light-grey-3">
                    Source
                  </td>
                  <td className="px-4 py-6 text-gray-900 text-[16px] border-b border-light-grey-3">
                    {device.source || "Unknown"}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-6 font-bold text-[16px] text-gray-700 border-b border-light-grey-3">
                    fcmToken
                  </td>
                  <td className="px-4 py-6 text-gray-900 text-[16px] border-b border-light-grey-3">
                    {device.fcmToken || "Unknown"}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-6 font-bold text-[16px] text-gray-700 border-b border-light-grey-3">
                    OS
                  </td>
                  <td className="px-4 py-6 text-gray-900 text-[16px] border-b border-light-grey-3">
                    {device.os || "Unknown"}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-6 font-bold text-[16px] text-gray-700 border-b border-light-grey-3">
                    deviceUniqueId
                  </td>
                  <td className="px-4 py-6 text-gray-900 text-[16px] border-b border-light-grey-3">
                    {device.deviceUniqueId || "Unknown"}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-6 font-bold text-[16px] text-gray-700 border-b border-light-grey-3">
                    model
                  </td>
                  <td className="px-4 py-6 text-gray-900 text-[16px] border-b border-light-grey-3">
                    {device.model || "Unknown"}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-6 font-bold text-[16px] text-gray-700 border-b border-light-grey-3">
                    Browser
                  </td>
                  <td className="px-4 py-6 text-gray-900 text-[16px] border-b border-light-grey-3">
                    {device.browser || "Unknown"}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-6 font-bold text-[16px] text-gray-700 border-b border-light-grey-3">
                    sourceDevice
                  </td>
                  <td className="px-4 py-6 text-gray-900 text-[16px] border-b border-light-grey-3">
                    {device.sourceDevice || "Unknown"}
                  </td>
                </tr>
              </tbody>
            ))}
          </table>
        </div>
      ) : (
        <div className="text-center py-40 text-gray-500">
          <p>No devices found for this user</p>
        </div>
      )}
    </div>
  );
};
export default DeviceList;
